import React, { useCallback, useMemo, useRef, useState } from 'react';
import { Navigate, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useAccount } from '../../contexts/AccountContext';
import { HomeSidebar, HomeTopBar } from '../../components/home/HomeHubParts';
import homeStyles from '../../components/home/HomeHub.module.css';
import { useAdminTheme } from './ui/useAdminTheme';
import { SECTION_META, SECTION_TABS, sectionFromPath } from './adminNavConfig';
import '../../styles/admin/adminTheme.css';

function canEnterAdmin(account) {
  return account.role === 'admin' || account.staff?.status === 'active' || Boolean(account.staff?.superAdmin);
}

function firstAllowedStaffPath(navSections) {
  if (!Array.isArray(navSections)) return '/home';
  for (const id of navSections) {
    const tab = SECTION_TABS.find((item) => item.id === id);
    if (tab?.path) return tab.path;
  }
  return '/home';
}

export default function AdminLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const { theme } = useAdminTheme();
  const { account, isLoading } = useAccount();
  const [navOpen, setNavOpen] = useState(false);
  const [childMeta, setChildMeta] = useState({});

  const refreshRef = useRef(() => {});
  const activeSection = sectionFromPath(location.pathname);
  const baseMeta = SECTION_META[activeSection] || SECTION_META.overview;
  const sectionChildMeta = childMeta[activeSection] || {};

  const registerShellMeta = useCallback((meta) => {
    setChildMeta((prev) => ({
      ...prev,
      [activeSection]: { ...prev[activeSection], ...meta },
    }));
  }, [activeSection]);

  const setRefreshHandler = useCallback((fn) => {
    refreshRef.current = typeof fn === 'function' ? fn : () => {};
  }, []);

  const outletContext = useMemo(
    () => ({
      account,
      activeSection,
      registerShellMeta,
      setRefreshHandler,
    }),
    [account, activeSection, registerShellMeta, setRefreshHandler]
  );

  const title = sectionChildMeta.title || baseMeta.title;

  if (!account) {
    if (isLoading) {
      return (
        <div className="vx-adminRoot" data-theme={theme}>
          Verifying access…
        </div>
      );
    }
    return <Navigate to="/login" replace />;
  }
  if (!canEnterAdmin(account)) return <Navigate to="/home" replace />;

  const isLimitedStaff = account.staff?.status === 'active' && !account.staff?.superAdmin && account.role !== 'admin';
  if (isLimitedStaff) {
    const allowed = account.staff?.navSections || [];
    if (!allowed.includes(activeSection)) {
      return <Navigate to={firstAllowedStaffPath(allowed)} replace />;
    }
  }

  return (
    <div className={homeStyles.hubRoot}>
      <HomeSidebar
        open={navOpen}
        onClose={() => setNavOpen(false)}
        activePath={location.pathname}
        onNavigate={(to) => {
          setNavOpen(false);
          navigate(to);
        }}
      />
      <div className={homeStyles.hubMain}>
        <HomeTopBar
          title={title}
          onMenu={() => setNavOpen(true)}
          showNotifications={false}
        />
        <div
          className={`${homeStyles.hubContent} ${homeStyles.hubContentWide} vx-adminRoot`}
          data-theme={theme}
        >
          <Outlet context={outletContext} />
        </div>
      </div>
    </div>
  );
}
